import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppState } from '@/hooks/useAppState';
import { useFinanceRefresh } from '@/hooks/useFinanceRefresh';
import { financeTimeline } from '@/mocks/finance';
import FinanceHotspotCard from '@/components/feature/FinanceHotspotCard';
import FinanceSkeleton from '@/components/feature/FinanceSkeleton';
import FinanceStaleNotice from '@/components/feature/FinanceStaleNotice';
import SharePreview from '@/components/feature/SharePreview';
import PrivacyBadge from '@/components/feature/PrivacyBadge';
import type { FinanceHotspot } from '@/mocks/finance';

const topicOptions = [
  { label: '全部', value: '全部' },
  { label: '宏观政策', value: '宏观政策' },
  { label: '科技与AI', value: '科技与AI' },
  { label: 'A股', value: 'A股' },
  { label: '港股与美股', value: '港股与美股' },
  { label: '新能源', value: '新能源' },
  { label: '消费', value: '消费' },
  { label: '公司动态', value: '公司动态' },
];

export default function Finance() {
  const { openShareModal, shareModalOpen, shareModalTarget, closeShareModal, showToast, clearToast, toastMessage } = useAppState();
  const [topicFilter, setTopicFilter] = useState('全部');
  const navigate = useNavigate();

  const {
    isRefreshing,
    isStale,
    currentItems,
    formattedLastUpdated,
    triggerRefresh,
    lastUpdatedAt,
    dataMode,
    refreshError,
  } = useFinanceRefresh((mode, message) => {
    showToast(mode === 'live' ? '已更新今日财经热点' : message || refreshError || '当前为财经演示数据');
    setTimeout(() => clearToast(), 3000);
  });

  const todayHighlights = useMemo(
    () => currentItems.filter((h) => h.heat === '高').slice(0, 3),
    [currentItems]
  );

  const topicHotspots = useMemo(() => {
    if (topicFilter === '全部') return currentItems.slice(3);
    return currentItems.filter((h) => h.tags.includes(topicFilter));
  }, [currentItems, topicFilter]);

  const staleMinutes = useMemo(() => {
    if (!lastUpdatedAt) return 0;
    return Math.floor((Date.now() - lastUpdatedAt.getTime()) / 60000);
  }, [lastUpdatedAt]);

  const shareTarget = shareModalTarget
    ? currentItems.find((h) => h.id === shareModalTarget) || null
    : null;

  return (
    <div className="min-h-screen bg-background-50 pb-20">
      {/* Header with refresh */}
      <header
        className="px-4 pt-4 pb-3"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-foreground-800 font-heading">财经</h1>
            <p className="text-xs text-foreground-400 mt-0.5">
              今天值得花时间关注的热点
            </p>
          </div>
          <button
            onClick={triggerRefresh}
            disabled={isRefreshing}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
              isRefreshing
                ? 'bg-background-100 text-foreground-400'
                : 'bg-primary-50 text-primary-600 hover:bg-primary-100'
            }`}
          >
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className={`ri-refresh-line ${isRefreshing ? 'animate-spin' : ''}`} />
            </span>
            {isRefreshing ? '更新中…' : '更新热点'}
          </button>
        </div>

        {/* Update status line */}
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <span className="flex items-center gap-1 text-[10px] text-foreground-400">
            <span
              className={`w-1.5 h-1.5 rounded-full ${isStale ? 'bg-amber-400' : 'bg-accent-400'}`}
            />
            {formattedLastUpdated}
          </span>
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-background-100 text-foreground-400">
            {dataMode === 'live' ? '实时数据' : '演示数据'}
          </span>
          <span className="text-[10px] text-foreground-400">
            共 {currentItems.length} 条热点
          </span>
          {refreshError && (
            <span className="text-[10px] text-amber-600 font-medium">
              {refreshError}
            </span>
          )}
          {!isRefreshing && isStale && (
            <span className="text-[10px] text-amber-600 font-medium">
              可能已过期
            </span>
          )}
        </div>

        {/* Stale notice */}
        {isStale && !isRefreshing && (
          <div className="mt-2">
            <FinanceStaleNotice staleMinutes={staleMinutes} onRefresh={triggerRefresh} />
          </div>
        )}
      </header>

      <main className="px-4 space-y-5">
        {/* Section 1: Today's Highlights */}
        <section>
          <div className="flex items-center gap-2 mb-2">
            <h2 className="text-sm font-semibold text-foreground-800 font-heading">
              今日重点
            </h2>
            <PrivacyBadge type="shareable" />
          </div>
          <div className="space-y-2.5">
            {isRefreshing ? (
              <>
                <FinanceSkeleton />
                <FinanceSkeleton />
                <FinanceSkeleton />
              </>
            ) : (
              todayHighlights.map((hotspot) => (
                <FinanceHotspotCard
                  key={hotspot.id}
                  hotspot={hotspot}
                  onShare={(h) => openShareModal(h.id)}
                />
              ))
            )}
          </div>
        </section>

        {/* Section 2: Browse by Topic */}
        <section>
          <h2 className="text-sm font-semibold text-foreground-800 mb-2 font-heading">
            按主题浏览
          </h2>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {topicOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setTopicFilter(opt.value)}
                className={`text-[10px] px-2.5 py-1 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap ${
                  topicFilter === opt.value
                    ? 'bg-foreground-900 text-background-50'
                    : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="space-y-2.5">
            {isRefreshing ? (
              <>
                <FinanceSkeleton />
                <FinanceSkeleton />
              </>
            ) : (
              topicHotspots.slice(0, 4).map((hotspot) => (
                <FinanceHotspotCard
                  key={hotspot.id}
                  hotspot={hotspot}
                  compact
                  onShare={(h) => openShareModal(h.id)}
                />
              ))
            )}
          </div>
          {!isRefreshing && topicHotspots.length > 4 && (
            <button className="w-full text-xs text-foreground-400 py-3 hover:text-foreground-600 transition-colors cursor-pointer whitespace-nowrap">
              查看更多 ({topicHotspots.length - 4} 条)
            </button>
          )}
        </section>

        {/* Section 3: Timeline */}
        <section>
          <h2 className="text-sm font-semibold text-foreground-800 mb-2 font-heading">
            热点时间线
          </h2>
          <div className="relative pl-5">
            <div className="absolute left-2 top-0 bottom-0 w-px bg-background-200" />
            <div className="space-y-3">
              {financeTimeline.map((item, i) => (
                <button
                  key={i}
                  onClick={() => navigate(`/finance/${item.id}`)}
                  className="relative block pl-4 py-1 cursor-pointer group w-full text-left"
                >
                  <div
                    className={`absolute left-[-18px] top-2 w-2.5 h-2.5 rounded-full border-2 ${
                      item.heat === '高'
                        ? 'border-red-400 bg-red-50'
                        : item.heat === '中'
                          ? 'border-amber-400 bg-amber-50'
                          : 'border-background-300 bg-background-100'
                    }`}
                  />
                  <span className="text-[10px] text-foreground-400 mb-0.5 block">
                    {item.time}
                  </span>
                  <p className="text-xs text-foreground-700 leading-snug group-hover:text-primary-600 transition-colors">
                    {item.title}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Section 4: My Watchlist */}
        <section className="pb-4">
          <div className="flex items-center gap-2 mb-2">
            <h2 className="text-sm font-semibold text-foreground-800 font-heading">
              我的关注
            </h2>
            <PrivacyBadge type="private" />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {['AI 芯片', '机器人', '新能源车', '消费电子', '量化宽松', '碳中和'].map(
              (tag) => (
                <span
                  key={tag}
                  className="text-[10px] px-2.5 py-1.5 rounded-full bg-primary-50 text-primary-600 font-medium cursor-pointer hover:bg-primary-100 transition-colors whitespace-nowrap"
                >
                  {tag}
                </span>
              )
            )}
          </div>
          <p className="text-[10px] text-foreground-300 mt-2">
            这些是你的私密关注标签，不会对外公开
          </p>
        </section>
      </main>

      {/* Share Preview */}
      {shareTarget && (
        <SharePreview
          hotspot={shareTarget}
          open={shareModalOpen}
          onClose={closeShareModal}
        />
      )}

      {/* Toast */}
      {toastMessage && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 animate-fade-in">
          <div className="px-4 py-2 rounded-lg bg-foreground-800 text-background-50 text-xs font-medium shadow-lg flex items-center gap-2">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-check-line text-sm" />
            </span>
            {toastMessage}
          </div>
        </div>
      )}
    </div>
  );
}
